import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Modal,
  Input,
  Select,
  Button,
  MultiSelect
} from '../../../components';
import Api from '../../../Api';
import '../../../components/CompanyForm/style.css'; 

export default function HolidayModal({ isOpen, onClose, initialData, onSuccess, companies = [] }) {
  const isEdit = !!initialData;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    date: '',
    repeat_yearly: false,
    type: 'FERIADO',
    abono: 'DIA_INTEIRO',
    apply_to_all_stations: false,
    company_ids: [],
    service_station_ids: []
  });

  const [stations, setStations] = useState([]);
  const [loadingStations, setLoadingStations] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        company_ids: initialData.companies ? initialData.companies.map(c => c.id) : [],
        service_station_ids: initialData.serviceStations ? initialData.serviceStations.map(s => s.id) : [],
        repeat_yearly: !!initialData.repeat_yearly,
        apply_to_all_stations: !!initialData.apply_to_all_stations
      });
    } else {
      setFormData({
        description: '',
        date: '',
        repeat_yearly: false,
        type: 'FERIADO',
        abono: 'DIA_INTEIRO',
        apply_to_all_stations: false,
        company_ids: [],
        service_station_ids: []
      });
    }
  }, [initialData, isOpen]);

  useEffect(() => {
    async function loadStations() {
      if (formData.company_ids.length === 0) {
        setStations([]);
        // Clear selected stations if no companies are selected
        setFormData(prev => ({ ...prev, service_station_ids: [] }));
        return;
      }
      
      setLoadingStations(true);
      try {
        const promises = formData.company_ids.map(cId => Api.get(`/service-stations/company/${cId}`));
        const results = await Promise.all(promises);
        
        const allStations = [];
        const seenIds = new Set();
        const validStationIds = new Set();
        
        results.forEach(res => {
            const list = res.data || []; 
            list.forEach(s => {
                if (!seenIds.has(s.id)) {
                    seenIds.add(s.id);
                    validStationIds.add(s.id);
                    const label = s.name || s.trade_name || `Posto #${s.id}`;
                    allStations.push({ ...s, label: label });
                }
            });
        });
        
        setStations(allStations);
        
        // Remove stations that no longer belong to selected companies
        setFormData(prev => ({
            ...prev,
            service_station_ids: prev.service_station_ids.filter(id => validStationIds.has(id))
        }));

      } catch (error) {
        console.error('Error loading stations', error);
        toast.error('Erro ao carregar postos.');
      } finally {
        setLoadingStations(false);
      }
    }

    if (isOpen) {
        loadStations();
    }
  }, [formData.company_ids, isOpen]);

  function handleChange(e) {
    const { id, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: type === 'checkbox' ? checked : value
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!formData.description) return toast.error('Descrição é obrigatória');
    if (!formData.date) return toast.error('Data é obrigatória');
    if (formData.company_ids.length === 0) return toast.error('Selecione pelo menos uma empresa');

    setLoading(true);
    try {
      const payload = {
        ...formData,
      };

      if (isEdit) {
        await Api.put(`/holidays/${initialData.id}`, payload);
        toast.success('Feriado atualizado com sucesso!');
      } else {
        await Api.post('/holidays', payload);
        toast.success('Feriado criado com sucesso!');
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao salvar feriado.');
    } finally {
      setLoading(false);
    }
  }
  

  
  // Revised Logic mimicking FileManager pattern but adapted for clean state
  function onCompaniesChange(newValues) {
      const allValue = 'all';
      const allIds = companies.map(c => c.id);
      const currentIds = formData.company_ids;
      
      // Determine if 'all' was visually selected before
      const wasFull = currentIds.length === companies.length && companies.length > 0;
      const wasAllInList = newValues.includes(allValue); // Did user just select it?
      
      // If 'all' is present in newValues:
      // It means user explicitly selected it (since we don't store it in state, it comes from click)
      // OR we passed it in props and it's still there?
      // I will handle the "pass 'all' in props" strategy.
      
      // Strategy:
      // 1. In render, pass `value={ isFull ? ['all', ...ids] : ids }`
      // 2. In onChange(newValues):
      //    a. `isAllSelected` = newValues.includes('all')
      //    b. `wasAllSelected` = wasFull
      
      const isAllSelected = newValues.includes(allValue);
      
      if (isAllSelected && !wasFull) {
          // Case: User clicked 'all' to select everything
          setFormData(prev => ({ ...prev, company_ids: allIds }));
      } else if (!isAllSelected && wasFull) {
          // Case: User clicked 'all' to deselect everything (it was present, now gone)
          setFormData(prev => ({ ...prev, company_ids: [] }));
      } else if (wasFull && newValues.length < (allIds.length + 1)) { 
          // Case: All was selected (so 'all' + IDs were in visual). 
          // User deselected a specific item. 'all' is still in `newValues`? 
          // Wait, if I pass `['all', ...ids]`, and user unchecks ID=1.
          // `newValues` will be `['all', ...ids_minus_1]`.
          // So `isAllSelected` is true. `wasFull` is true.
          // We need to remove 'all' and the deselected item.
          const realIds = newValues.filter(v => v !== allValue);
          setFormData(prev => ({ ...prev, company_ids: realIds }));
      } else {
          // Normal selection/deselection
          const realIds = newValues.filter(v => v !== allValue);
          setFormData(prev => ({ ...prev, company_ids: realIds }));
      }
  }
  
  function onStationsChange(newValues) {
      const allValue = 'all';
      const allIds = stations.map(s => s.id);
      const currentIds = formData.service_station_ids;
      
      const wasFull = currentIds.length === stations.length && stations.length > 0;
      const isAllSelected = newValues.includes(allValue);
      
      if (isAllSelected && !wasFull) {
          setFormData(prev => ({ ...prev, service_station_ids: allIds }));
      } else if (!isAllSelected && wasFull) {
          setFormData(prev => ({ ...prev, service_station_ids: [] }));
      } else if (wasFull && newValues.length < (allIds.length + 1)) {
          const realIds = newValues.filter(v => v !== allValue);
          setFormData(prev => ({ ...prev, service_station_ids: realIds }));
      } else {
          const realIds = newValues.filter(v => v !== allValue);
          setFormData(prev => ({ ...prev, service_station_ids: realIds }));
      }
  }

  const typeOptions = [
    { value: 'FERIADO', label: 'FERIADO' },
    { value: 'PONTO_FACULTATIVO', label: 'PONTO FACULTATIVO' }
  ];

  const abonoOptions = [
    { value: 'DIA_INTEIRO', label: 'DIA INTEIRO' },
    { value: 'MEIO_PERIODO_MANHA', label: 'MEIO PERÍODO (MANHÃ)' },
    { value: 'MEIO_PERIODO_TARDE', label: 'MEIO PERÍODO (TARDE)' },
    { value: 'NENHUM', label: 'NENHUM' }
  ];
  
  const companyOptions = [
      { value: 'all', label: 'TODOS' },
      ...companies.map(c => ({
          value: c.id,
          label: c.trade_name || c.corporate_name
      }))
  ];
  
  const stationOptions = [
      { value: 'all', label: 'TODOS' },
      ...stations.map(s => ({
          value: s.id,
          label: s.label || s.name
      }))
  ];
  
  // Calculate display values
  const companyDisplayValues = formData.company_ids.length === companies.length && companies.length > 0
      ? ['all', ...formData.company_ids]
      : formData.company_ids;
      
  const stationDisplayValues = formData.service_station_ids.length === stations.length && stations.length > 0
      ? ['all', ...formData.service_station_ids]
      : formData.service_station_ids;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Editar Feriado' : 'Novo Feriado'}
    >
      <form onSubmit={handleSubmit} className="company-form-component">
         <div className="form-body">
             <div className="form-grid">
                <div className="full-width">
                    <Input
                    id="description"
                    label="Descrição do Feriado (*)"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Ex: Natal"
                    />
                </div>
                
                <Input
                    id="date"
                    type="date"
                    label="Data (*)"
                    value={formData.date}
                    onChange={handleChange}
                />

                <Select
                    id="type"
                    label="Tipo (*)"
                    value={formData.type}
                    onChange={handleChange}
                    options={typeOptions}
                />

                <Select
                    id="abono"
                    label="Período de Abono (*)"
                    value={formData.abono}
                    onChange={handleChange}
                    options={abonoOptions}
                />
                
                <div style={{ display: 'flex', alignItems: 'center', height: '100%', paddingTop: '1.8rem' }}>
                     <div className="checkbox-group" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input
                            type="checkbox"
                            id="repeat_yearly"
                            checked={formData.repeat_yearly}
                            onChange={handleChange}
                            style={{ width: 16, height: 16, cursor: 'pointer' }}
                        />
                        <label htmlFor="repeat_yearly" style={{ cursor: 'pointer', userSelect: 'none', fontSize: '0.9rem', color: 'var(--text-primary)' }}>Repetir todo ano?</label>
                    </div>
                </div>
             </div>

             <h3 className="section-title">Empresas e Postos</h3>
             
             <div className="form-grid">
                <div className="full-width">
                    <MultiSelect
                        id="company_ids"
                        label="Empresas (*)"
                        options={companyOptions}
                        value={companyDisplayValues}
                        onChange={onCompaniesChange}
                        placeholder="Selecione as empresas..."
                    />
                </div>
                
                <div className="full-width">
                     <div className="checkbox-group" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <input
                            type="checkbox"
                            id="apply_to_all_stations"
                            checked={formData.apply_to_all_stations}
                            onChange={handleChange}
                            style={{ width: 16, height: 16, cursor: 'pointer' }}
                        />
                        <label htmlFor="apply_to_all_stations" style={{ cursor: 'pointer', userSelect: 'none', fontSize: '0.9rem', color: 'var(--text-primary)' }}>Aplicar a todos os Postos de Serviço</label>
                    </div>
                    
                    {!formData.apply_to_all_stations ? (
                        <MultiSelect
                            id="service_station_ids"
                            label="Postos Selecionados"
                            options={stationOptions}
                            value={stationDisplayValues}
                            onChange={onStationsChange}
                            placeholder={loadingStations ? "Carregando postos..." : "Selecione postos..."}
                            disabled={loadingStations}
                        />
                    ) : (
                        <div style={{ padding: 12, background: 'var(--background-color)', borderRadius: 6, border: '1px solid var(--border-color)', color: 'var(--text-secondary)', fontSize: '0.9rem', textAlign: 'center' }}>
                            Nenhum posto específico selecionado. O feriado será aplicado a <strong>TODOS</strong> os postos das empresas selecionadas.
                        </div>
                    )}
                </div>
             </div>
         </div>

         <div className="modal-actions">
          <Button onClick={onClose} className="cancel-button" disabled={loading} type="button">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} loading={loading} type="submit">
            Salvar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
